import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {ReactNode} from "react";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import {useSettingsHeaderContext} from "./SettingsHeaderContext";

export type SettingsPageHeaderProps = {
    title: string | null | undefined
    back: () => void
    actions?: ReactNode
    /** Positioned across the whole header, for content that takes it over temporarily. */
    overlay?: ReactNode
    loading?: boolean
    mobileOnly?: boolean
}

const SettingsPageHeader = ({
    title,
    back,
    actions,
    overlay,
    loading = false,
    mobileOnly = false
}: SettingsPageHeaderProps) => {
    const {hostElement, isMobile, contentScrolled} = useSettingsHeaderContext()
    const {t} = useTranslation()

    if (!hostElement || (mobileOnly && !isMobile)) return null

    const displayTitle = title === null
        ? t('Settings')
        : title?.trim() || '-'

    return createPortal(
        <Box
            component="header"
            sx={{
                position: 'relative',
                zIndex: theme => theme.zIndex.appBar,
                width: '100%',
                height: isMobile ? 56 : 72,
                flexShrink: 0,
                boxSizing: 'border-box',
                bgcolor: isMobile ? 'background.paper' : 'rgba(255, 255, 255, 0.32)',
                backdropFilter: isMobile ? undefined : 'blur(12px)',
                WebkitBackdropFilter: isMobile ? undefined : 'blur(12px)',
                borderBottom: '1px solid',
                borderColor: isMobile || contentScrolled ? 'grey.100' : 'transparent',
                transition: 'border-color 150ms',
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                gap={isMobile ? 1.5 : 1}
                sx={{
                    width: isMobile ? '100%' : 'calc(100% - 48px)',
                    height: '100%',
                    maxWidth: isMobile ? undefined : '900px',
                    mx: 'auto',
                    px: isMobile ? 1.5 : 0,
                    boxSizing: 'border-box',
                }}
            >
                <IconButton
                    size="small"
                    onClick={back}
                    disabled={loading}
                    aria-label={t('Back')}
                    sx={{
                        width: 36,
                        height: 36,
                        flexShrink: 0,
                        borderRadius: 1,
                        color: 'text.primary',
                    }}
                >
                    <ArrowBackIcon fontSize="small"/>
                </IconButton>

                <Typography
                    variant={isMobile ? 'h6' : 'h4'}
                    color="text.primary"
                    noWrap
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        fontWeight: isMobile ? 600 : undefined,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {displayTitle}
                </Typography>

                {actions && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={isMobile ? 0.5 : 1}
                        sx={{
                            flexShrink: 0,
                            maxWidth: isMobile ? '55%' : undefined,
                            overflowX: isMobile ? 'auto' : undefined,
                            scrollbarWidth: 'none',
                            '&::-webkit-scrollbar': {display: 'none'},
                        }}
                    >
                        {actions}
                    </Stack>
                )}
            </Stack>

            {overlay}

            {loading && (
                <LinearProgress sx={{position: 'absolute', left: 0, right: 0, bottom: 0}}/>
            )}
        </Box>,
        hostElement
    )
}

export default SettingsPageHeader
